import React, { useState } from 'react';
import { Plus, Trash2, Pencil, Check, X, Loader2 } from 'lucide-react';
import { fmtPct } from './gradeUtils';

const inputCls = 'bg-black border-2 border-zinc-800 focus:border-orange-500 outline-none px-2 py-1.5 text-xs text-white';

const EntryRow = ({ entry, onSave, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ label: entry.label || '', score: entry.score, max_score: entry.max_score });
  const pct = (100 * entry.score) / (entry.max_score || 100);
  if (edit) {
    return (
      <div className="flex flex-wrap items-center gap-2 py-1.5" data-testid={`entry-edit-${entry.id}`}>
        <input className={`${inputCls} flex-1 min-w-[120px]`} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="entry-edit-label" />
        <input type="number" className={`${inputCls} w-20`} value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} data-testid="entry-edit-score" />
        <span className="text-zinc-500 text-xs">/</span>
        <input type="number" className={`${inputCls} w-20`} value={f.max_score} onChange={(e) => setF({ ...f, max_score: e.target.value })} data-testid="entry-edit-max" />
        <button onClick={async () => { if (await onSave(entry, f)) setEdit(false); }} className="p-1.5 text-emerald-400 hover:text-emerald-300" data-testid="entry-edit-save"><Check className="w-4 h-4" /></button>
        <button onClick={() => setEdit(false)} className="p-1.5 text-zinc-500 hover:text-white" data-testid="entry-edit-cancel"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-zinc-900" data-testid={`entry-row-${entry.id}`}>
      <span className="text-xs text-zinc-300 truncate">{entry.label || 'Untitled'} <span className="text-zinc-600">· {entry.date}</span></span>
      <span className="flex items-center gap-3 shrink-0">
        <span className="text-[11px] text-zinc-500">{entry.score}/{entry.max_score}</span>
        <span className="text-xs font-black text-white w-14 text-right">{fmtPct(pct)}</span>
        <button onClick={() => setEdit(true)} className="p-1 text-zinc-500 hover:text-orange-400" data-testid={`entry-edit-btn-${entry.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        <button onClick={() => onDelete(entry)} className="p-1 text-zinc-500 hover:text-red-400" data-testid={`entry-delete-btn-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </span>
    </div>
  );
};

export const CategoryEntries = ({ category, entries, onAdd, onSave, onDelete }) => {
  const [f, setF] = useState({ label: '', score: '', max_score: 100 });
  const [busy, setBusy] = useState(false);
  const full = category.per_unit_max && entries.length >= category.per_unit_max;
  const avg = entries.length ? entries.reduce((a, e) => a + (100 * e.score) / e.max_score, 0) / entries.length : null;

  const submit = async (e) => {
    e.preventDefault();
    if (f.score === '') return;
    setBusy(true);
    const ok = await onAdd(category.key, f);
    setBusy(false);
    if (ok) setF({ label: '', score: '', max_score: f.max_score });
  };

  return (
    <div className="border-2 border-zinc-800 p-4" data-testid={`category-${category.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-black uppercase tracking-widest text-white">{category.label} <span className="text-orange-400">· {category.weight}%</span></div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
          {entries.length}{category.per_unit_max ? `/${category.per_unit_max}` : ''} · Avg <span className="text-white font-black" data-testid={`category-avg-${category.key}`}>{fmtPct(avg)}</span>
        </div>
      </div>
      {entries.map((e) => <EntryRow key={e.id} entry={e} onSave={onSave} onDelete={onDelete} />)}
      {!full && (
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2 mt-2">
          <input className={`${inputCls} flex-1 min-w-[140px]`} placeholder={`${category.short} name (optional)`} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid={`add-label-${category.key}`} />
          <input type="number" step="any" min="0" className={`${inputCls} w-20`} placeholder="Score" value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} data-testid={`add-score-${category.key}`} />
          <span className="text-zinc-500 text-xs">/</span>
          <input type="number" step="any" min="1" className={`${inputCls} w-20`} value={f.max_score} onChange={(e) => setF({ ...f, max_score: e.target.value })} data-testid={`add-max-${category.key}`} />
          <button type="submit" disabled={busy} className="flex items-center gap-1 bg-orange-500 hover:bg-orange-400 text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest disabled:opacity-50" data-testid={`add-submit-${category.key}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add
          </button>
        </form>
      )}
    </div>
  );
};
